import type { components } from './schema'

export type Snapshot = components['schemas']['SessionSnapshot']
export type SessionPage = components['schemas']['SessionPage']
export type SelectionPage = components['schemas']['SelectionPage']
export type TurnCreate = components['schemas']['TurnCreate']
export type DecisionCreate = components['schemas']['DecisionCreate']
export type Event =
  | components['schemas']['TurnEvent']
  | components['schemas']['RunEvent']
  | components['schemas']['DeltaEvent']
  | components['schemas']['MessageEvent']
  | components['schemas']['QuestionEvent']
  | components['schemas']['ToolEvent']
  | components['schemas']['ChangeEvent']
  | components['schemas']['SimulationEvent']
  | components['schemas']['ApprovalEvent']
  | components['schemas']['WorkEvent']
  | components['schemas']['UsageEvent']
// A piece of a reply as it streams. Sent live and never saved, so it has no seq.
export type LiveText = {
  type: 'live.delta'
  message_id: string
  offset: number
  text: string
}

const base = `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/setup-chat`
export class SetupError extends Error {
  status: number
  constructor(status: number) {
    super(`Setup request failed (${status})`)
    this.status = status
  }
}
export async function request<T>(
  token: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(`${base}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!response.ok) throw new SetupError(response.status)
  return response.json() as Promise<T>
}

// Decode streaming bytes before splitting SSE frames: a character or a CRLF can
// straddle any network chunk. Comments and multi-line data follow the SSE spec.
export async function readEvents(
  token: string,
  session: string,
  after: number,
  signal: AbortSignal,
  onEvent: (event: Event | LiveText) => void,
) {
  const response = await fetch(
    `${base}/sessions/${session}/events?after=${after}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      cache: 'no-store',
      signal,
    },
  )
  if (!response.ok) throw new SetupError(response.status)
  if (!response.body) throw new Error('Stream unavailable')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let data: string[] = []
  const line = (value: string) => {
    if (!value) {
      if (data.length) onEvent(JSON.parse(data.join('\n')) as Event | LiveText)
      data = []
    } else if (value.startsWith('data:'))
      data.push(value.slice(5).replace(/^ /, ''))
  }
  try {
    while (true) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value, { stream: !done })
      let end: number
      while ((end = buffer.search(/[\r\n]/)) !== -1) {
        if (buffer[end] === '\r' && end === buffer.length - 1 && !done) break
        const current = buffer.slice(0, end)
        const size = buffer[end] === '\r' && buffer[end + 1] === '\n' ? 2 : 1
        buffer = buffer.slice(end + size)
        line(current)
      }
      if (done) {
        if (buffer) line(buffer)
        break
      }
    }
  } finally {
    reader.releaseLock()
  }
}
